import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StorageService } from './storage.service';
import { OktaService } from './okta.service';
import * as Constants from "../utils/Constants";
import {
    calculateWindowArea,
    calculateFrameArea,
    calculateGlassEdge,
    calculateMuntinLength,
    extractFrameWidths,
    WindowDimensions
} from '../utils/calculation';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { MasterdataApiService } from './masterdata-api.service';
import { FRAME_MANUFACTURERS, SystemManufacturer } from '../utils/frame-brands.data';
import { findRatingIdByEnergyValue, getTranslatedPositionLabel } from '../utils/Constants';
import { App } from '@capacitor/app';
import { TemperatureService } from './temperature.service';
import { HttpService } from './http.service';

@Injectable({ providedIn: 'root' })
export class PdfPayloadService {

    private manufacturers: SystemManufacturer[] = [];

    constructor(
        private storageService: StorageService,
        private oktaService: OktaService,
        private translate: TranslateService,
        private masterdataApi: MasterdataApiService,
        private temperatureService: TemperatureService,
        private httpService: HttpService
    ) { }

    // ─────────────────────────────────────────────────────────────────────────────
    // FORMATTING UTILITIES
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Format UW value with 3 decimal places (from old Caluwin code)
     */
    private uwUnroundedToString(uw: number): string {
        let result = uw.toString();
        const decSepPos = result.indexOf('.');

        if (decSepPos > -1) {
            result = result.substr(0, Math.min(result.length, decSepPos + 1 + 3));
            result = result.padEnd(decSepPos + 4, '0');
        } else {
            result = result + '.';
            result = result.padEnd(result.length + 3, '0');
        }

        return result;
    }

    /**
     * Round to two decimal places
     */
    private roundTwoValuePositions(value: number): number {
        if (value === undefined || value === null) return 0;
        return Math.round(value * 100) / 100;
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // VALIDATION UTILITIES
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Check if glass thickness exceeds 4mm
     */
    private checkGlassThicknessWarning(glass: any): boolean {
        return (glass.exteriorGlassThickness > 4.0) || (glass.interiorGlassThickness > 4.0);
    }

    /**
     * Check Ug boundaries
     */
    private checkUgBoundaries(glass: any): boolean {
        return glass.ug !== undefined && glass.ug !== null;
    }

    /**
     * Check Uf boundaries
     */
    private checkUfBoundaries(frame: any): boolean {
        return frame.uf !== undefined && frame.uf !== null;
    }

    /**
     * Check if glass inset note should be shown
     */
    private checkUfForNoteGlassInset(frame: any): boolean {
        return frame.id === -1 && (frame.uf0 > 1.5 || frame.uf1 > 1.5);
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // FRAME HELPERS
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Load manufacturers from cache or local data
     */
    private async loadManufacturers(): Promise<SystemManufacturer[]> {
        if (this.manufacturers.length > 0) {
            return this.manufacturers;
        }

        const cached = await this.masterdataApi.getCachedMasterData();
        if (cached?.frameManufacturers?.length) {
            this.manufacturers = cached.frameManufacturers as SystemManufacturer[];
            console.log('Loaded manufacturers from masterdata:', this.manufacturers);
            return this.manufacturers;
        }

        this.manufacturers = FRAME_MANUFACTURERS;
        return this.manufacturers;
    }

    /**
     * Get frame manufacturer name
     */
    private async getFrameManName(frame: any): Promise<string> {
        if (!frame.system) return '';

        const manufacturerId = frame.system.manufacturerId ?? frame.system.mid ?? null;
        if (!manufacturerId) return '';

        const manufacturers = await this.loadManufacturers();
        const manufacturer = manufacturers.find(m => m.id === manufacturerId);

        return manufacturer?.name || '';
    }

    /**
     * Get frame system name
     */
    private async getFrameSystemName(frame: any): Promise<any> {
        if (!frame.system) return '';

        const manufacturerId = frame.system.manufacturerId ?? frame.system.mid ?? null;
        const systemId = frame.system.systemId ?? frame.system.sid ?? null;

        if (!manufacturerId || !systemId) return '';

        const manufacturers = await this.loadManufacturers();
        const manufacturer = manufacturers.find(m => m.id === manufacturerId);

        if (!manufacturer?.systems) return '';

        return manufacturer.systems.find(s => s.id === systemId) || null;
    }

    /**
     * Build frames list from frame data
     */
    private buildFramesList(frame: any, windowTypeId: number): any[] {
        // ── By-frame: id >= 1 → single entry, all positions share same Uf & width ──
        if (frame.id >= 1) {
            return [];
        }

        // ── Manual / By-brand: id === -1 → one entry per frame position ──
        const ufValues: number[] = frame.ufValues
            ?? Array.from({ length: 7 }, (_, i) => frame[`uf${i}`] ?? 0);
        const wValues: number[] = frame.wValues
            ?? Array.from({ length: 7 }, (_, i) => frame[`w${i}`] ?? 0);

        const frameCounts: { [key: number]: number } = {
            1: 3, 2: 4, 3: 7, 4: 6, 5: 5, 6: 7,
            7: 5, 8: 6, 9: 3, 10: 4, 11: 5, 12: 7,
            13: 4, 14: 4, 15: 4, 16: 6
        };
        const frameCount = frameCounts[windowTypeId] || 4;

        return Array.from({ length: frameCount }, (_, i) => {
            const fullLabel = getTranslatedPositionLabel(windowTypeId, `f${i}`, this.translate);
            return {
                name: fullLabel.trim(),         // full label including subtitle e.g. "Lateral (left)"
                uf: parseFloat((ufValues[i] ?? 0).toString()),
                w: parseFloat((wValues[i] ?? 0).toString()),
            };
        });
    }
    // ─────────────────────────────────────────────────────────────────────────────
    // WINDOW TYPE HELPERS
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Get mapped window type from constants
     */
    private getMappedWindowType(windowTypeId: number): any {
        if (windowTypeId === undefined || windowTypeId === null) return null;
        return Constants.data_windows?.find((w: any) => w.id === windowTypeId) ?? null;
    }

    /**
     * Get translated window type name
     */
    private getWindowTypeName(windowTypeId: number): string {
        const mappedType = this.getMappedWindowType(windowTypeId);
        if (!mappedType?.name) return 'Unknown Type';
        return this.translate.instant(mappedType.name);
    }

    /**
     * Get window type image path
     */
    private getWindowTypeImagePath(windowTypeId: number): string {
        const mappedType = this.getMappedWindowType(windowTypeId);
        return mappedType?.img || '';
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // CALCULATION HELPERS
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Calculate window Psi value
     */
    private calculateWindowPsi(p: any): number {
        const spacerPsi = p.spacer?.psi || 0;
        return spacerPsi;
    }

    /**
     * Get condensation rating from comfort calculator
     */
    private getCondensationRating(comfortCalc: any): string {
        if (!comfortCalc) return this.translate.instant('NOT_CALCULATED');
        let res = "";
        const surfaceTemp = parseFloat(comfortCalc?.surfaceTemperature);
        const dewPoint = parseFloat(comfortCalc?.dewPoint);
        const moldTemperature = parseFloat(comfortCalc?.moldTemperature);

        if (isNaN(surfaceTemp) || isNaN(dewPoint)) {
            res = this.translate.instant('NOT_CALCULATED');
            return res;
        }

        if (surfaceTemp <= dewPoint) {
            res = this.translate.instant('WARNING_CONDENSATION') + " ";
        } else {
            res = this.translate.instant('NO_CONDENSATION') + " ";
        }

        if (surfaceTemp <= moldTemperature) {
            res = res + ',' + this.translate.instant('WARNING_MOULD') + " ";
        } else {
            res = res + ',' + this.translate.instant('NO_MOULD') + " ";
        }
        return res;
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // WARNING GENERATION
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Generate warnings based on configuration
     */
    private generateWarnings(p: any): string[] {
        const warnings: string[] = [];
        const glass = p.windowglas || {};
        const frame = p.windowframe || {};

        if (this.checkGlassThicknessWarning(glass)) {
            warnings.push(this.translate.instant('WARNING_PSI_GLASS'));
        }

        if (!this.checkUgBoundaries(glass)) {
            warnings.push(this.translate.instant('WARNING_PSI_UG'));
        }

        if (!this.checkUfBoundaries(frame)) {
            warnings.push(this.translate.instant('WARNING_PSI_UF'));
        }

        if ((glass.ug || 0) > 1.0) {
            warnings.push(this.translate.instant('WARNING_UG_HIGH'));
        }

        return warnings;
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // PDF GENERATION
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Generate PDF payload from stored project data
     */
    async generatePdfPayload(options: {
        showSGLogo?: boolean;
        pdfTitle?: string;
        pdfSubject?: string;
        generateUWReport?: boolean;
        generateWerReport?: boolean;
        generateSWSReport?: boolean;
        generateComfortCalculator?: boolean;
        language?: string;
    } = {}): Promise<any> {

        const projectRaw = await this.storageService.getProject();
        const p = projectRaw ? JSON.parse(projectRaw) : null;

        if (!p) throw new Error('No project data in storage');

        const appVersion = await this.getAppVersion();
        const userClaims = await this.oktaService.userInfo();
        const pdfCreator = userClaims?.['name'] as string || userClaims?.['email'] as string || '';
        const saveOnlyGlass: boolean = p?.saveOnlyGlass || false;

        const {
            showSGLogo = true,
            pdfTitle = '',
            pdfSubject = '',
            generateUWReport = false,
            generateWerReport = false,
            generateSWSReport = false,
            generateComfortCalculator = false,
            language
        } = options;
        const pdfLanguage = language || 'en-GB';
        // Set language for translations
        // await this.translate.use(language).toPromise();

        // Build payload based on saveOnlyGlass flag
        const payload: any = {
            showSGLogo,
            projectName: p.projectName,
            pdfTitle,
            pdfSubject,
            appVersion,
            pdfCreator,
            generateUWReport,
            generateWerReport,
            generateSWSReport,
            generateComfortCalculator,
            language: pdfLanguage
        };

        // Conditionally add uwCalculator based on saveOnlyGlass flag
        if (!saveOnlyGlass) {
            payload.uwCalculator = await this.buildUwCalculator(p);
        }

        // Always add swsCalculator 
        payload.swsCalculator = this.buildSwsCalculator(p);

        console.log('PDF Payload:', JSON.stringify(payload, null, 2));
        return payload;
    }

    /**
     * Send PDF generation request to API
     */
    async generatePdf(options: {
        showSGLogo?: boolean;
        pdfTitle?: string;
        pdfSubject?: string;
        generateUWReport?: boolean;
        generateWerReport?: boolean;
        generateSWSReport?: boolean;
        generateComfortCalculator?: boolean;
        language?: string;
    } = {}): Promise<any> {

        const payload = await this.generatePdfPayload(options);

        const url = environment.caluwin_api.config_base_url + environment.caluwin_api.endpoints.generatePdf;
        const headers = {
            'Content-Type': 'application/json'
        };

        const response = await this.httpService.executeAnonymous('POST', {
            url,
            headers,
            data: payload,
            responseType: 'arraybuffer' as any
        });

        console.log('Generate PDF response:', response.status, response.data);
        if (response.status < 200 || response.status >= 300) {
            throw new Error(`HTTP ${response.status}`);
        }
        const base64Data = response.data as string;

        // derive file name
        const projectRaw = await this.storageService.getProject();
        const p = projectRaw ? JSON.parse(projectRaw) : null;
        const projectName = p?.projectName || 'caluwin-report';
        const fileName = `${projectName}.pdf`;

        return { bytes: base64Data, fileName };
    }

    /**
     * Build UW Calculator section
     */
    private async buildUwCalculator(p: any): Promise<any> {
        if (!p) return null;

        const frame = p.windowframe || {};
        const glass = p.windowglas || {};
        const spacer = p.spacer || {};
        const muntin = p.muntin || {};
        const dimensions: WindowDimensions = {
            a: parseFloat(p.dimensions?.a || 0),
            b: parseFloat(p.dimensions?.b || 0),
            c: parseFloat(p.dimensions?.c || 0)
        };

        const windowTypeId = parseInt(p.windowtype || 1);
        const frameWidths = extractFrameWidths(frame);

        const muntinType = { id: parseInt(p.muntin?.type?.id || 0) };
        const muntinShape = {
            id: parseInt(p.muntin?.shape?.id || 0),
            countHorizontal: parseInt(p.muntin?.shape?.countHorizontal || 0),
            countVertical: parseInt(p.muntin?.shape?.countVertical || 0)
        };
        const psiValue = p.psi || 0;
        console.log('====================================');
        console.log(psiValue);
        console.log('====================================');
        // Calculate areas
        const windowArea = calculateWindowArea(windowTypeId, dimensions);
        const frameArea = calculateFrameArea(windowTypeId, dimensions, frameWidths);
        const glassArea = windowArea - frameArea;
        const frameFraction = parseFloat(((frameArea * 100) / windowArea).toFixed(0));

        // Calculate lengths
        const glassEdge = calculateGlassEdge(windowTypeId, dimensions, frameWidths);
        const muntinLength = calculateMuntinLength(windowTypeId, dimensions, frameWidths, muntinType, muntinShape);

        // Get frame list
        const frames = this.buildFramesList(frame, windowTypeId);

        // Format UW detail
        const uwValue = p.uw || 0;
        const uwDetail = this.uwUnroundedToString(uwValue);

        // Get frame system names
        const frameManName = await this.getFrameManName(frame);
        // const frameSystemName = await this.getFrameSystemName(frame);
        const system = await this.getFrameSystemName(frame);
        // Extract WER results
        const werResult = p.windowEnergyRatingResult || {};
        const ratingEnergyColor = await this.getEnergyColor(werResult);

        const temperatureUnit = await this.temperatureService.getTemperatureUnit();
        const unitTemperature = temperatureUnit === 'fahrenheit' ? 'F' : 'C';

        const getClimateZoneDisplayName = (value: string): string => {
            const zone = Constants.climateZones.find((z) => z.value === value);
            return zone ? zone.displayName : value;
        };
        return {
            windowType: windowTypeId,
            windowTypeName: this.getWindowTypeName(windowTypeId),
            windowTypeImgPath: this.getWindowTypeImagePath(windowTypeId),
            dimension: {
                a: this.metersToMmString(dimensions.a),
                b: this.metersToMmString(dimensions.b),
                c: this.metersToMmString(dimensions.c)
            },
            windowGlass: glass.name || '',
            spacerName: spacer.name || '',
            windowSpacerExpert: p.spacerExpert ? {
                sealant: {
                    id: p.spacerExpert.sealant?.id || 0,
                    name: p.spacerExpert.sealant?.name || '',
                    conductivity: this.roundTwoValuePositions(p.spacerExpert.sealant?.conductivity || 0),
                    size: p.spacerExpert.sealant?.size || 0
                },
                desiccant: {
                    id: p.spacerExpert.desiccant?.id || 0,
                    name: p.spacerExpert.desiccant?.name || ''
                }
            } : null,
            frameName: frame.name || '',
            frameSystem: {
                man: frameManName,
                name: system?.name || '',
                variant: system?.variant?.trim()
                    ? this.translate.instant(system.variant.trim())
                    : '',
                type: this.translate.instant(frame?.type)
            },
            frameUf: this.roundTwoValuePositions(frame.uf || 0).toFixed(3),
            frameWidth: frame.id === -1 ? '-' : (frame.visibleWidth?.toFixed(3) || '0.000'),
            frames: frames,
            areaGlass: glassArea.toFixed(3),
            areaFrame: frameArea.toFixed(3),
            areaWindow: windowArea.toFixed(3),
            frameFraction: frameFraction.toString(),
            glassUg: (glass.ug || 0).toFixed(3),
            glassInt: (glass.interiorGlassThickness || 0).toFixed(3),
            glassExt: (glass.exteriorGlassThickness || 0).toFixed(3),
            windowPsi: psiValue,
            noteGlassInset: this.checkUfForNoteGlassInset(frame) ? 'NOTE_GLASS_INSET_18' : '',
            muntinPsi: this.roundTwoValuePositions(muntin.psi || 0).toString(),
            lengthGlassEdge: glassEdge.toFixed(3),
            lengthMuntins: muntinLength.toFixed(3),
            muntinShape: this.translate.instant(muntin?.shape?.name || ''),
            muntinName: muntin?.shape?.id === 1 ? '' : this.translate.instant(muntin?.type?.name || ''),
            muntinSpacer: muntin?.shape?.id === 1 ? '' : this.translate.instant(muntin?.spacer || ''),
            fRsi: spacer?.fRsi || '',
            unitTemperature,
            ti: this.convertTempByUnit(p.comfortCalculator?.ti, temperatureUnit),
            te: this.convertTempByUnit(p.comfortCalculator?.te, temperatureUnit),
            phi: p.comfortCalculator?.phi?.toString() || '',
            tsi: this.convertTempByUnit(p.comfortCalculator?.surfaceTemperature, temperatureUnit),
            tdp: this.convertTempByUnit(p.comfortCalculator?.dewPoint, temperatureUnit),
            condensationResult: this.getCondensationRating(p.comfortCalculator),
            uw: p.uw?.toFixed(2) || '',
            uwDetail: uwDetail,
            sw: (glass.gValue || 0).toFixed(3),
            warning: this.generateWarnings(p),
            wer_ch: werResult?.chResult ? {
                a: (werResult?.chResult.a || 0).toFixed(3),
                b: (werResult?.chResult.b || 0).toFixed(3),
                ag: this.formatUpTo3(werResult?.chResult.ag),
                af: this.formatUpTo3(werResult?.chResult.af),
                aw: this.formatUpTo3(werResult?.chResult.aw),
                uw: this.formatUpTo3(werResult?.chResult.uw),
                hw: this.formatUpTo3(werResult?.chResult.hw),
                hs: this.formatUpTo3(werResult?.chResult.hs),
                uweq: (werResult?.chResult?.uweq || 0),
                effClass: werResult?.chResult.rating || '',
                effClassColor: ratingEnergyColor
            } : null,
            wer_fr: werResult?.frResult ? {
                badge_z1: werResult?.frResult.badge_z1 || '',
                value_z1: (werResult?.frResult.value_z1 || 0).toFixed(3),
                badge_z2: werResult?.frResult.badge_z2 || '',
                value_z2: (werResult?.frResult.value_z2 || 0).toFixed(3),
                badge_z3: werResult?.frResult.badge_z3 || '',
                value_z3: (werResult?.frResult.value_z3 || 0).toFixed(3),
                badge_confort_z1: werResult?.frResult.badge_confort_z1 || '',
                value_confort_z1: (werResult?.frResult.value_confort_z1 || 0).toFixed(3),
                badge_confort_z2: werResult?.frResult.badge_confort_z2 || '',
                value_confort_z2: (werResult?.frResult.value_confort_z2 || 0).toFixed(3),
                badge_confort_z3: werResult?.frResult.badge_confort_z3 || '',
                value_confort_z3: (werResult?.frResult.value_confort_z3 || 0).toFixed(3),
                tlw: (werResult?.frResult.tlw || 0).toFixed(3),
                sw: (werResult?.frResult.sw || 0).toFixed(3),
                fabricant: werResult?.frResult.fabricant || '',
                modele: werResult?.frResult.modele || ''
            } : null,
            wer_dk: werResult?.dkResult ? {
                gg: (werResult?.dkResult.gg || 0).toFixed(3),
                gw: (werResult?.dkResult.gw || 0).toFixed(3),
                eref: (werResult?.dkResult.eref || 0).toFixed(3),
                werclass: werResult?.dkResult.werclass || '',
                werclassColor: ratingEnergyColor
            } : null,
            phe: werResult.pheResult ? {
                climateZone: this.translate.instant(getClimateZoneDisplayName(werResult.pheResult.climateZone || '')),
                ug: (werResult.pheResult.ug || 0).toFixed(3),
                psiOpaque: (werResult.pheResult.psiOpaque || 0).toFixed(3),
                uwPhi: (werResult.pheResult.uwPhi || 0).toFixed(3),
                effClass: werResult.pheResult.effClass || '',
                isWindowValid: werResult.pheResult.isWindowValid || false
            } : null
        };
    }

    private getEnergyColor(werResult: any): any {
        const country = werResult.country;
        let energyRatings: any = [], energyValue;

        if (country == 'Schweiz') {
            energyRatings = Constants.ENERGY_RATINGS_SCHEWIZ;
            energyValue = werResult.chResult?.uweq;
        } else if (country == 'Dänemark') {
            energyRatings = Constants.ENERGY_RATINGS_DENMARK;
            energyValue = werResult.dkResult?.eref;
        } else if (country == 'Frankreich') {
            energyValue = werResult.frResult?.sw;
        } else if (country == 'Großbritannien') {
            energyRatings = Constants.ENERGY_RATINGS_UK;
            energyValue = werResult.ukResult?.wer;
        } else if (country == 'Other') {
            energyRatings = Constants.ENERGY_RATINGS_PASSIVE;
            energyValue = werResult.pheResult?.psiOpaque;
        }

        const rating = findRatingIdByEnergyValue(energyValue, energyRatings);
        return rating?.color;
    }

    /**
     * Build SWS Calculator section
     */
    private buildSwsCalculator(p: any): any {
        if (!p || !p.airSimulationResult) return null;

        const sws = p.airSimulationResult;

        return {
            structure: p.windowglas?.structure || '',
            shortEdge: (sws.shortEdge || 0),
            longEdge: (sws.longEdge || 0),
            lifetime: p.lifetime ? {
                installationPlace: p.lifetime.installationPlace || '',
                latitude: p.lifetime.latitude.toString() || '',
                longitude: p.lifetime.longitude.toString() || '',
                rating: this.translate.instant(sws.lifetimeResult) || ''
            } : {
                installationPlace: '',
                latitude: '',
                longitude: '',
                rating: ''
            },
            elevatedTransport: p.elevatedTransport ? {
                meanOfTransport: p.elevatedTransport?.transportLabel || '',
                maxHeightDifference: p.elevatedTransport?.maxHeightDiff?.toString() || '',
                rating: this.translate.instant(sws.transportResult) || ''
            } : {
                meanOfTransport: '',
                maxHeightDifference: '',
                rating: ''
            }
        };
    }

    private metersToMmString(value: number | null | undefined): string {
        if (value === null || value === undefined || isNaN(Number(value))) return '0';
        return (Number(value) * 1000).toFixed(0);
    }

    /**
     * Get app version
     */
    private async getAppVersion(): Promise<string> {
        try {
            const info = await App.getInfo();
            return info.version || '1.0.0';
        } catch (error) {
            console.warn('Could not get app version:', error);
            return '1.0.0';
        }
    }

    private formatUpTo3(value: number | undefined | null): number {
        return Number((value ?? 0).toFixed(3));
    }

    private convertTempByUnit(value: number | string | undefined | null, unit: 'celsius' | 'fahrenheit'): string {
        if (value === undefined || value === null || value === '') return '';

        const c = Number(value);
        if (isNaN(c)) return '';

        const converted = unit === 'fahrenheit'
            ? this.temperatureService.celsiusToFahrenheit(c)
            : c;

        return Number(converted.toFixed(1)).toString();
    }
}